import type { ReactNode } from 'react'
import React, { Suspense, useMemo } from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useSuspenseFragment } from 'react-relay-offline'
import { useBreakpoint } from '@vtex/admin-ui'

import type { ProductSkuImage_Product$key } from './__generated__/ProductSkuImage_Product.graphql'
import { ProductSkuImageLoading } from './ProductSkuImageLoading'
import { useProductPageUrlParams } from '../../hooks'
import { ProductSkuImageMobile } from './ProducSkuImageMobile'
import { ProducSkuImageDesktop } from './ProducSkuImageDesktop'

const productFragment = graphql`
  fragment ProductSkuImage_Product on Product {
    skus {
      edges {
        node {
          id
          ...ProducSkuImageMobile_ProductSku
          ...ProducSkuImageDesktopProps_ProductSku
        }
      }
    }
  }
`

type ProductSkuImageContentProps = {
  productRef: ProductSkuImage_Product$key
  children: ReactNode
}

const ProductSkuImageContent = ({
  productRef,
  ...props
}: ProductSkuImageContentProps) => {
  const product = useSuspenseFragment(productFragment, productRef)
  const { selectedSkuId } = useProductPageUrlParams()
  const { breakpoint } = useBreakpoint()
  const isMobileOrTablet = ['mobile', 'tablet'].includes(breakpoint)

  const selectedSku = useMemo(
    () => product.skus.edges.find((sku) => sku.node.id === selectedSkuId),
    [product.skus.edges, selectedSkuId]
  )

  if (!selectedSku) {
    return null
  }

  if (isMobileOrTablet) {
    return <ProductSkuImageMobile productSkuRef={selectedSku.node} {...props} />
  }

  return <ProducSkuImageDesktop productSkuRef={selectedSku.node} {...props} />
}

export type ProductSkuImageProps = {
  productRef: ProductSkuImage_Product$key
  children: ReactNode
}

export const ProductSkuImage = (props: ProductSkuImageProps) => (
  <Suspense fallback={<ProductSkuImageLoading />}>
    <ProductSkuImageContent {...props} />
  </Suspense>
)
